import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

// GET /public/{slug}/file/{id}

const PublicFileDetailSchema = z.object({
  id: z.number(),
  entity_type: z.string().nullable(),
  entity_id: z.string().nullable(),
  entity_name: z.string().nullable(),
  file_name: z.string(),
  source: z.string(),
  file_size: z.number(),
  age: z.string(),
  uploaded_date: z.string(),
  uploaded_by: z.string().nullable(),
  last_updated_at: z.string(),
  last_updated_by: z.string().nullable(),
  description: z.string().nullable(),
  url: z.string(),
  thumbnail_url: z.string()
});

export type PublicFileDetail = z.infer<typeof PublicFileDetailSchema>;

const RequestSchema = z.object({
  slug: z.string(),
  id: z.string()
});

type RequestType = z.infer<typeof RequestSchema>;

const QUERY_KEY = ["publicFileDetail"] as const;

const fetcher = (params: RequestType) =>
  api<RequestType, PublicFileDetail>({
    method: "GET",
    url: `/public/:slug/file/:id`,
    requestSchema: RequestSchema,
    responseSchema: PublicFileDetailSchema,
    body: params
  });

type UsePublicFileOptions = {
  variables: RequestType;
  enabled?: boolean;
};

export function usePublicFile({
  variables,
  enabled = true
}: UsePublicFileOptions) {
  return useQuery({
    queryKey: [...QUERY_KEY, variables] as const,
    queryFn: () => fetcher(variables),
    enabled
  });
}

export function usePublicFileSuspense(variables: RequestType) {
  return useSuspenseQuery({
    queryKey: [...QUERY_KEY, variables],
    queryFn: () => fetcher(variables)
  });
}

export const publicFileQuery = {
  queryKey: QUERY_KEY,
  fetcher
};
